import { useEffect } from 'react';
import { createReconnectingEventSource } from '../lib/reconnectingEventSource';
import { refreshAccessToken } from '../services/auth';

/**
 * useFormTaskSseBus — singleton EventSource hook for the form-tasks SSE stream.
 *
 * <p>RC-224 — Before this hook existed, every consumer (FormsToDoPage, the
 * future left-nav badge, the global toast notifier) would open its own
 * EventSource to {@code /api/v1/form-tasks/stream}. Browsers cap SSE
 * connections per origin (~6 in Chrome/Firefox), and an unmounted page
 * could leak a connection if it forgot to close. The singleton + refcount
 * pattern gives every consumer a clean subscribe/unsubscribe API while
 * the underlying connection is opened ONCE per tab and closed when the
 * last subscriber drops.</p>
 *
 * <h3>Usage</h3>
 * <pre>
 *   useFormTaskSseBus('form_task_assigned', (event, payload) =&gt; {
 *     console.log('new assignment:', payload);
 *   });
 * </pre>
 *
 * The hook handles add/remove of the listener on mount/unmount. Callers
 * never touch EventSource directly.
 *
 * <h3>Module-level state</h3>
 * Persists across React tree changes (navigation, remounts) — exactly
 * what we want for "the connection stays open while at least one page
 * cares about form_task events". The state is per-window, not per-React-
 * root, so the singleton survives any future React 18 strict-mode double-
 * mount or React Server Components transitions.
 *
 * <h3>Auto-reconnect</h3>
 * EventSource's built-in retry handles transient network drops. We don't
 * surface those to subscribers — the next event after reconnect arrives
 * naturally. On terminal errors (e.g. 401 after session expiry) we close
 * the connection so a fresh subscribe re-opens it (the browser may have
 * recovered by then). No exponential-backoff layer in JS — the platform
 * already does that.
 *
 * <h3>Heartbeat / connected events</h3>
 * The BE stream emits {@code heartbeat} every 15s and {@code connected}
 * once on attach. Both are intentionally NOT exposed to subscribers —
 * they exist to keep the connection alive through proxies and to confirm
 * attach, not to signal data changes. Subscribers asking for those event
 * names get nothing fired.
 *
 * <h3>Disabling</h3>
 * Pass a falsy event name (e.g. when the consumer hasn't loaded yet) to
 * skip subscription entirely — common pattern when the auth state isn't
 * ready and we don't want to open a connection that'll 401.
 *
 * @param {string|null} eventName  Which named event to subscribe to (e.g.
 *   'form_task_assigned'). Falsy = no-op.
 * @param {(event: string, payload: Object) => void} handler  Called with
 *   the event name + parsed JSON payload. Handler identity matters —
 *   each unique function reference is one subscription slot.
 */
export default function useFormTaskSseBus(eventName, handler) {
  useEffect(() => {
    if (!eventName || typeof handler !== 'function') return undefined;
    const unsubscribe = subscribe(eventName, handler);
    return unsubscribe;
  }, [eventName, handler]);
}

// ──────────────────────────────────────────────────────────────────────
//  Singleton module state
//  Lives outside the React tree so the connection persists across page
//  navigations + survives Strict-Mode double mounting.
// ──────────────────────────────────────────────────────────────────────

const STREAM_URL = '/api/v1/form-tasks/stream';
/** Per-event-name set of subscriber callbacks. */
const subscribers = new Map(); // eventName → Set<handler>
/** The one reconnecting connection for this tab (or null when no one listens).
 *  All storm-safety (take-control-on-error + capped exponential backoff) lives
 *  in the shared createReconnectingEventSource helper — single source of truth. */
let conn = null;
/** Grace window before tearing the connection down after the LAST subscriber
 *  leaves. It spans React's synchronous unmount→remount (and Strict-Mode's
 *  double-invoke), so a consumer remount loop can't thrash the connection
 *  open/closed. The shared wrapper's capped backoff only protects a *persistent*
 *  connection; without this grace, every remount span spins up a fresh
 *  EventSource that fires one immediate attempt at an unreachable backend —
 *  turning a render loop into a request storm (hundreds of /form-tasks/stream
 *  hits per second) that overflows the backend's TCP accept backlog (errno 111).
 *  A genuine navigation away from every form surface still closes it, ~2s later. */
const CLOSE_GRACE_MS = 2000;
let closeTimer = null;
/** Parse+dispatch wrappers, kept so we can removeEventListener cleanly. The
 *  helper re-applies these across reconnects, so the bus never re-attaches. */
const wrappedListeners = new Map(); // eventName → wrappedFn

/**
 * Register a callback for an SSE event. Returns an unsubscribe function.
 * Opens the EventSource on the first subscription; closes it when the
 * last subscriber unsubscribes.
 */
function subscribe(eventName, handler) {
  let bucket = subscribers.get(eventName);
  if (!bucket) {
    bucket = new Set();
    subscribers.set(eventName, bucket);
  }
  bucket.add(handler);
  ensureConn();
  ensureListenerAttached(eventName);

  return () => {
    const b = subscribers.get(eventName);
    if (!b) return;
    b.delete(handler);
    if (b.size === 0) {
      subscribers.delete(eventName);
      detachListener(eventName);
    }
    if (subscribers.size === 0) {
      scheduleClose();
    }
  };
}

function ensureConn() {
  // A re-subscribe within the grace window cancels any pending teardown, so a
  // consumer remount loop (unsubscribe → resubscribe in the same tick) reuses
  // the SAME connection instead of thrashing a fresh one open/closed. This is
  // what lets the wrapper's capped backoff actually engage under churn.
  if (closeTimer != null) { clearTimeout(closeTimer); closeTimer = null; }
  if (conn != null) return;
  // withCredentials so the JWT cookie rides along. The helper owns the
  // reconnect state machine: it takes control of EVERY error (hard 401/403
  // close AND backend-unreachable / nginx 502), closing the stream itself and
  // reconnecting on capped exponential backoff + jitter — so an unreachable
  // backend can never produce the native-EventSource request storm again.
  conn = createReconnectingEventSource({
    url: STREAM_URL,
    withCredentials: true,
    // Self-heal the auth gap: EventSource can't carry a Bearer header and
    // can't trigger api.js's 401-refresh, so an expired 8h session cookie used
    // to 403 the stream forever. On a HARD close the helper refreshes the
    // cookie via the same path fetch uses, then reconnects with the fresh
    // cookie. Capped + single-flight inside the helper; if the refresh token
    // is also dead, refreshAccessToken() drives the normal session-expired UX.
    recover: refreshAccessToken,
  });
}

function ensureListenerAttached(eventName) {
  if (!conn) return;
  if (wrappedListeners.has(eventName)) return;
  const wrapper = (msgEvent) => {
    const bucket = subscribers.get(eventName);
    if (!bucket || bucket.size === 0) return;
    let payload = null;
    if (typeof msgEvent.data === 'string' && msgEvent.data.length > 0) {
      try {
        payload = JSON.parse(msgEvent.data);
      } catch {
        // Non-JSON payload (heartbeat-style ping) — pass through as raw string.
        payload = msgEvent.data;
      }
    }
    // Iterate over a snapshot — a handler may unsubscribe itself, mutating
    // the live set mid-iteration.
    Array.from(bucket).forEach((cb) => {
      try { cb(eventName, payload); }
      catch (err) {
        // One bad handler MUST NOT poison the rest.
        // eslint-disable-next-line no-console
        console.error('[useFormTaskSseBus] handler threw for', eventName, err);
      }
    });
  };
  conn.addEventListener(eventName, wrapper);
  wrappedListeners.set(eventName, wrapper);
}

function detachListener(eventName) {
  if (!conn) return;
  const wrapper = wrappedListeners.get(eventName);
  if (!wrapper) return;
  conn.removeEventListener(eventName, wrapper);
  wrappedListeners.delete(eventName);
}

/** Defer teardown by CLOSE_GRACE_MS instead of closing synchronously when the
 *  last subscriber leaves, so a same-tick unsubscribe→resubscribe (remount loop,
 *  Strict-Mode double-invoke) is absorbed without dropping the connection.
 *  ensureConn() cancels this the moment a subscriber returns. Idempotent: a
 *  second drain-to-zero while a close is already pending is a no-op. */
function scheduleClose() {
  if (closeTimer != null) return;
  closeTimer = setTimeout(() => {
    closeTimer = null;
    if (subscribers.size === 0) closeConnection();
  }, CLOSE_GRACE_MS);
}

function closeConnection() {
  // Cancel any pending deferred close first, then tear down. close() inside the
  // helper cancels its own pending backoff reconnect, so no stray timer remains.
  if (closeTimer != null) { clearTimeout(closeTimer); closeTimer = null; }
  if (!conn) return;
  conn.close();
  conn = null;
  wrappedListeners.clear();
}

// ──────────────────────────────────────────────────────────────────────
//  Test-only escape hatches — exported under a __TEST__ namespace so
//  unit tests can reset state between cases without resorting to
//  jest.resetModules() (which would invalidate the singleton's whole
//  point of being module-level state).
// ──────────────────────────────────────────────────────────────────────

export const __TEST__ = {
  reset() {
    subscribers.clear();
    closeConnection();
  },
  subscribersFor(eventName) {
    return subscribers.get(eventName);
  },
  hasOpenConnection() {
    return conn != null;
  },
};
